import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RichTextEditorComponent } from '../../RsdForms/CreateMoment/components/RichTextEditorComponent';
import { selectHierarchyFilters } from '../../../pages/Home/slice/selectors';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import ChevronDownFigma from '../Overview/icons/chevron-down-figma.svg';
import { AudienceDropdownPortal } from './AudienceDropdownPortal';
import { MustWinDropdownPortal } from './MustWinDropdownPortal';
import '../styles/figmaPixelPerfectOverviewField.css';
import { isEmptyHtml } from '../CommentPanel/utils';
import styles from './AudienceStrategicAlignmentField.module.scss';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';

interface AudienceStrategicAlignmentValue {
  audienceStrategyMappings: Record<string, string[]>; // { "Creative Pros": ["option1", ...], ... }
  content: string;
}

interface AudienceStrategicAlignmentFieldProps {
  value: AudienceStrategicAlignmentValue;
  onChange: (value: AudienceStrategicAlignmentValue) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  savedContent?: string;
  lastUpdated?: string;
}

export const AudienceStrategicAlignmentField: React.FC<
  AudienceStrategicAlignmentFieldProps
> = ({
  value = { audienceStrategyMappings: {}, content: '' },
  onChange,
  label,
  placeholder = 'Enter details here...',
  disabled = false,
  onSubmit,
  onCancel,
  savedContent,
  lastUpdated
}) => {
  // Get data from Redux store
  const hierarchyFilters = useSelector(selectHierarchyFilters);
  const audienceOptions = hierarchyFilters.customerAudiences || [];
  const audienceStrategyMappings =
    hierarchyFilters.audienceStrategyMappings || {};

  // State for dropdowns
  const [isAudienceDropdownOpen, setIsAudienceDropdownOpen] = useState(false);
  const [isMustWinDropdownOpen, setIsMustWinDropdownOpen] = useState(false);

  // State for rich text editor
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const [textContent, setTextContent] = useState(value.content || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  // Refs
  const audienceDropdownRef = useRef<HTMLDivElement>(null);
  const mustWinDropdownRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLDivElement>(null);
  const contentMeasureRef = useRef<HTMLDivElement>(null);
  const fieldContainerRef = useRef<HTMLDivElement>(null);
  const htmlContentRef = useRef<HTMLDivElement>(null);

  const MAX_COLLAPSED_HEIGHT = 288;

  // Get selected audiences from value
  const selectedAudiences = Object.keys(value.audienceStrategyMappings || {});

  // Handle audience selection
  const toggleAudience = (audience: string) => {
    const currentMappings = { ...value.audienceStrategyMappings };

    if (selectedAudiences.includes(audience)) {
      // Remove audience and its mappings
      delete currentMappings[audience];
    } else {
      // Add audience with empty array
      currentMappings[audience] = [];
    }

    onChange({
      ...value,
      audienceStrategyMappings: currentMappings
    });
  };

  // Handle must-win option selection
  const toggleMustWinOption = (audience: string, option: string) => {
    const currentMappings = { ...value.audienceStrategyMappings };
    const audienceOptions = currentMappings[audience] || [];

    if (audienceOptions.includes(option)) {
      // Remove option
      currentMappings[audience] = audienceOptions.filter(opt => opt !== option);
      // Keep audience even if no options left (set to empty array)
      // Don't delete the audience - it should remain selected
    } else {
      // Add option (ensure audience exists)
      if (!currentMappings[audience]) {
        currentMappings[audience] = [];
      }
      currentMappings[audience] = [...audienceOptions, option];
    }

    onChange({
      ...value,
      audienceStrategyMappings: currentMappings
    });
  };

  // Get display text for audience dropdown
  const getAudienceDisplayText = () => {
    return 'Audience';
  };

  // Get display text for must-win dropdown
  const getMustWinDisplayText = () => {
    return 'Must Win';
  };

  // Build grouped options for must-win dropdown (memoized for performance)
  const groupedMustWinOptions = useMemo(() => {
    const grouped: Record<string, string[]> = {};

    // Only show options for selected audiences
    selectedAudiences.forEach(audience => {
      if (audienceStrategyMappings[audience]) {
        grouped[audience] = audienceStrategyMappings[audience];
      }
    });

    return grouped;
  }, [selectedAudiences, audienceStrategyMappings]);

  // Sync textContent with value changes
  useEffect(() => {
    setTextContent(value.content || '');
  }, [value.content]);

  // Measure content height
  useEffect(() => {
    const measureHeight = () => {
      if (contentMeasureRef.current) {
        setContentHeight(contentMeasureRef.current.scrollHeight);
      }
    };

    measureHeight();
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [textContent, isTextEditorOpen]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Close audience dropdown
      if (
        isAudienceDropdownOpen &&
        audienceDropdownRef.current &&
        !audienceDropdownRef.current.contains(target)
      ) {
        const dropdownElement = document.querySelector(
          '.audience-dropdown-portal'
        );
        if (!dropdownElement || !dropdownElement.contains(target)) {
          setIsAudienceDropdownOpen(false);
        }
      }

      // Close must-win dropdown
      if (
        isMustWinDropdownOpen &&
        mustWinDropdownRef.current &&
        !mustWinDropdownRef.current.contains(target)
      ) {
        const dropdownElement = document.querySelector(
          '.mustwin-dropdown-portal'
        );
        if (!dropdownElement || !dropdownElement.contains(target)) {
          setIsMustWinDropdownOpen(false);
        }
      }
    };

    if (isAudienceDropdownOpen || isMustWinDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isAudienceDropdownOpen, isMustWinDropdownOpen]);

  // Handle text area click
  const handleTextAreaClick = (e: React.MouseEvent) => {
    if (disabled) return;

    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.closest('a')) {
      return;
    }

    e.stopPropagation();
    setIsTextEditorOpen(true);
  };

  // Handle text change
  const handleTextChange = (newContent: string) => {
    setTextContent(newContent);
    onChange({
      ...value,
      content: newContent
    });
  };

  const isDirectlyOpenableHref = (href: string): boolean =>
    /^(https?:|mailto:|tel:|blob:|data:|about:)/i.test(href);

  // Handle link clicks in rendered HTML
  const handleLinkClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      const anchor = target as HTMLAnchorElement;
      const href = anchor.getAttribute('href');

      if (href) {
        event.preventDefault();
        event.stopPropagation();

        if (href.startsWith('mailto:') || href.startsWith('tel:')) {
          window.location.href = href;
        } else if (isDirectlyOpenableHref(href)) {
          window.open(href, '_blank', 'noopener,noreferrer');
        } else {
          const urlToOpen =
            href.startsWith('www.') || href.includes('.')
              ? `https://${href}`
              : href;
          window.open(urlToOpen, '_blank', 'noopener,noreferrer');
        }
      }
    }
  };

  // Add link click listener
  useEffect(() => {
    const currentRef = htmlContentRef.current;
    if (currentRef) {
      currentRef.addEventListener('click', handleLinkClick, true);
      return () => {
        currentRef.removeEventListener('click', handleLinkClick, true);
      };
    }
  }, []);

  return (
    <div
      className="figma-pixel-perfect-overview-field"
      ref={fieldContainerRef}
      style={{ padding: 0 }}
    >
      {/* Label Section */}
      <div className="figma-label-section">
        <div className="figma-label-text">{label}</div>
      </div>

      {/* Content Section */}
      <div className="figma-content-section">
        {/* Two Dropdowns Side by Side */}
        <div className={styles.dropdownsContainer}>
          {/* Audience Dropdown */}
          <div className={styles.dropdownWrapper}>
            <div className="figma-picker-container">
              <div
                ref={audienceDropdownRef}
                className={`figma-picker-field ${styles.dropdownField} ${
                  disabled ? styles.disabled : ''
                }`}
                onClick={() => {
                  if (disabled) return;
                  setIsAudienceDropdownOpen(!isAudienceDropdownOpen);
                }}
              >
                <div className="figma-picker-text-stack">
                  <div className="figma-picker-value">
                    {getAudienceDisplayText()}
                  </div>
                </div>
                <div className="figma-picker-right-container">
                  <div className="figma-picker-chevron">
                    <img
                      src={ChevronDownFigma}
                      alt="Chevron Down"
                      width={10}
                      height={6}
                    />
                  </div>
                </div>
              </div>

              {/* Audience Dropdown Portal */}
              {isAudienceDropdownOpen && !disabled && (
                <AudienceDropdownPortal
                  audienceOptions={audienceOptions}
                  selectedAudiences={selectedAudiences}
                  onToggleAudience={toggleAudience}
                />
              )}
            </div>
          </div>

          {/* Must Win Dropdown */}
          <div className={styles.dropdownWrapper}>
            <div className="figma-picker-container">
              <ToolTipWrapper
                message={
                  selectedAudiences.length === 0 && !disabled
                    ? 'Please select an audience first'
                    : ''
                }
              >
                <div
                  ref={mustWinDropdownRef}
                  className={`figma-picker-field ${styles.dropdownField} ${
                    disabled || selectedAudiences.length === 0
                      ? styles.disabled
                      : ''
                  }`}
                  onClick={() => {
                    if (disabled) return;
                    if (selectedAudiences.length === 0) return;
                    setIsMustWinDropdownOpen(!isMustWinDropdownOpen);
                  }}
                >
                  <div className="figma-picker-text-stack">
                    <div className="figma-picker-value">
                      {getMustWinDisplayText()}
                    </div>
                  </div>
                  <div className="figma-picker-right-container">
                    <div className="figma-picker-chevron">
                      <img
                        src={ChevronDownFigma}
                        alt="Chevron Down"
                        width={10}
                        height={6}
                      />
                    </div>
                  </div>
                </div>
              </ToolTipWrapper>

              {/* Must Win Dropdown - absolute positioning relative to trigger */}
              {isMustWinDropdownOpen &&
                !disabled &&
                selectedAudiences.length > 0 && (
                  <MustWinDropdownPortal
                    groupedMustWinOptions={groupedMustWinOptions}
                    audienceStrategyMappings={
                      value.audienceStrategyMappings || {}
                    }
                    onToggleMustWinOption={toggleMustWinOption}
                  />
                )}
            </div>
          </div>
        </div>

        {/* Display Selected Items Grouped by Audience - Matching Figma Layout */}
        {selectedAudiences.length > 0 && (
          <div className={styles.displayContainer}>
            {selectedAudiences.map((audience, index) => {
              const audienceOptions =
                value.audienceStrategyMappings[audience] || [];

              return (
                <div key={audience}>
                  <div
                    className={`${styles.displayRow} ${
                      audienceOptions.length === 0
                        ? styles.displayRowNoOptions
                        : ''
                    }`}
                  >
                    {/* Left Column - Audience Name */}
                    <div className={styles.audienceColumn}>
                      <div className={styles.audienceName}>{audience}</div>
                    </div>

                    {/* Right Column - Must Win Options as Bullet Points */}
                    <div className={styles.mustWinColumn}>
                      {audienceOptions.length > 0 ? (
                        <ul className={styles.mustWinOptionsList}>
                          {audienceOptions.map(option => (
                            <li key={option} className={styles.mustWinOption}>
                              {option}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className={styles.noOptionsPlaceholder}>
                          None selected
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Divider between audience groups */}
                  {index < selectedAudiences.length - 1 && (
                    <div className={styles.divider} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Rich Text Editor */}
        <div
          className={`figma-text-content-area ${styles.textArea} ${
            disabled ? styles.disabled : ''
          }`}
          ref={textAreaRef}
          onClick={handleTextAreaClick}
        >
          {!isTextEditorOpen ? (
            <>
              <div
                ref={contentMeasureRef}
                className={`figma-text-display ${styles.textDisplay} ${
                  !textContent ? 'figma-text-placeholder' : ''
                } ${
                  !isExpanded && contentHeight > MAX_COLLAPSED_HEIGHT
                    ? styles.collapsed
                    : ''
                }`}
                style={{
                  maxHeight:
                    !isExpanded && contentHeight > MAX_COLLAPSED_HEIGHT
                      ? `${MAX_COLLAPSED_HEIGHT}px`
                      : 'none'
                }}
              >
                <div
                  ref={htmlContentRef}
                  dangerouslySetInnerHTML={{
                    __html: isEmptyHtml(textContent) ? placeholder : textContent
                  }}
                />
              </div>
              {contentHeight > MAX_COLLAPSED_HEIGHT && (
                <button
                  className="figma-read-more-button"
                  onClick={e => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                >
                  {isExpanded ? 'Read Less' : 'Read More'}
                </button>
              )}
            </>
          ) : (
            <div onClick={e => e.stopPropagation()}>
              <RichTextEditorComponent
                field={{
                  id: 'audienceStrategicAlignment',
                  name: 'audienceStrategicAlignment',
                  label,
                  required: false,
                  type: 'richtext'
                }}
                value={textContent}
                onChange={(_, newValue) => handleTextChange(newValue)}
                externalTimestamp={lastUpdated}
                minHeight="300px"
                hideLabel
                footer={
                  <div className={styles.footerButtons}>
                    <button
                      className={styles.cancelButton}
                      onClick={e => {
                        e.stopPropagation();
                        if (onCancel) {
                          onCancel();
                        }
                        setTextContent(savedContent || value.content || '');
                        setIsTextEditorOpen(false);
                      }}
                      aria-label="Cancel"
                    >
                      Cancel
                    </button>
                    <button
                      className={styles.saveButton}
                      onClick={async e => {
                        e.stopPropagation();
                        setIsTextEditorOpen(false);
                        if (onSubmit) {
                          await onSubmit();
                        }
                      }}
                      aria-label="Save"
                    >
                      Save
                    </button>
                  </div>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
